import { useMemo, useState } from 'react';
import {
  Eye,
  Pencil,
  LockKeyhole,
  LockOpen,
  Mail,
  PlusCircle,
  RefreshCcw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  UserRoundCheck,
  UserRoundX,
  UsersRound,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';
import {
  useUserAudit,
  useUserDetails,
  useUserManagementMutations,
  useSubscriptionAddOns,
  useSubscriptionTiers,
  useUserSubscription,
  useUserSubscriptionPricing,
  useUsers,
} from '../../hooks/useUserManagement';
import { AdminCreateUserRequest, UserSummary, UserStatus } from '../../types/userManagement';
import {
  ActivateUserModal,
  AssignAddOnsModal,
  AssignTierModal,
  AuditHistoryModal,
  CreateUserModal,
  DeactivateUserModal,
  EditUserModal,
  ForcePasswordResetModal,
  ResendInvitationModal,
  SuspendUserModal,
  UnlockUserModal,
  ViewUserModal,
} from '../../components/user-management/UserManagementModals';

type UserAction =
  | 'create'
  | 'edit'
  | 'view'
  | 'activate'
  | 'deactivate'
  | 'suspend'
  | 'unlock'
  | 'reset'
  | 'invite'
  | 'tier'
  | 'addons'
  | 'audit'
  | null;

type SortField = 'name' | 'email' | 'createdDate';

const PAGE_SIZE = 10;
const USER_STATUSES: UserStatus[] = [
  'Active',
  'Inactive',
  'Suspended',
  'PendingSetup',
];

const STATUS_CLASSES: Record<UserStatus, string> = {
  Active: 'bg-emerald-50 text-emerald-700',
  Inactive: 'bg-slate-100 text-slate-600',
  Suspended: 'bg-red-50 text-red-700',
  PendingSetup: 'bg-amber-50 text-amber-700',
};

export default function UserManagementModule() {
  const { claims } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'All'>('All');
  const [sortField, setSortField] = useState<SortField>('name');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<UserSummary | null>(null);
  const [activeAction, setActiveAction] = useState<UserAction>(null);
  const [error, setError] = useState('');

  const mutations = useUserManagementMutations();
  const usersQuery = useUsers({
    page: currentPage,
    pageSize: PAGE_SIZE,
    search: searchTerm || undefined,
    status: statusFilter === 'All' ? undefined : statusFilter,
    brandingId: claims?.brandingId || undefined,
  });
  const userDetailsQuery = useUserDetails(selectedUser?.id);
  const tiersQuery = useSubscriptionTiers();
  const addOnsQuery = useSubscriptionAddOns();
  const subscriptionQuery = useUserSubscription(selectedUser?.id);
  const pricingQuery = useUserSubscriptionPricing(selectedUser?.id);
  const audit = useUserAudit(activeAction === 'audit' ? selectedUser?.id : undefined);
  const isSaving = Object.values(mutations).some((mutation) => mutation.isPending);
  const detailedUser = userDetailsQuery.data ?? toUserDetails(selectedUser);
  const tiers = (tiersQuery.data ?? []).filter((tier) => tier.isActive);
  const addOns = (addOnsQuery.data ?? []).filter((addOn) => addOn.isActive);

  const filteredUsers = useMemo(() => {
    return [...(usersQuery.data?.items ?? [])].sort(
        (left, right) => {
        switch (sortField) {
            case 'name':
            return `${left.firstName ?? ''} ${left.lastName ?? ''}`
                .trim()
                .localeCompare(
                `${right.firstName ?? ''} ${right.lastName ?? ''}`.trim()
                );

            case 'email':
            return (left.email ?? '').localeCompare(
                right.email ?? ''
            );

            case 'createdDate':
            return (
                new Date(right.createdDate ?? 0).getTime() -
                new Date(left.createdDate ?? 0).getTime()
            );

            default:
            return 0;
        }
        }
    );
  }, [sortField, usersQuery.data?.items]);

  const totalPages = Math.max(1, Math.ceil((usersQuery.data?.total ?? 0) / PAGE_SIZE));

  function openAction(action: UserAction, user?: UserSummary) {
    setSelectedUser(user ?? null);
    setActiveAction(action);
    setError('');
  }

  function closeModal() {
    setActiveAction(null);
    setSelectedUser(null);
    setError('');
  }

  async function runAction(action: () => Promise<unknown>, successMessage: string) {
    try {
      await action();
      toast.success(successMessage);
      closeModal();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Unable to update this user.'));
    }
  }

  function updateStatus(status: UserStatus, suspensionReason?: string) {
    if (!selectedUser) return;

    runAction(async () => {
      await mutations.updateStatus.mutateAsync({
        userId: selectedUser.id,
        request: { newStatus: status, reason: suspensionReason },
      });
    }, 'User status updated.');
  }

  return (
    <div className="min-h-screen p-6 pt-10 md:p-8 lg:p-10">
      <header className="mb-8 mt-5 flex flex-col justify-between gap-5 md:flex-row md:items-center">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-800">
            <UsersRound className="text-strivre-burgundy" size={24} />
            User Management
          </h1>
          <p className="mt-1 text-sm text-slate-500">Provision users and manage account access.</p>
        </div>
        <button className="btn-primary btn-md flex items-center gap-2" onClick={() => openAction('create')}>
          <PlusCircle size={18} />
          Create user
        </button>
      </header>

      <div className="mb-6 rounded-2xl border border-strivre-lightnavy bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end text-left">
            
            {/* Search */}
            <div className="flex-1">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Search
            </label>

            <div className="relative">
                <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                className="input-base w-full pl-10"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(event) => {
                    setSearchTerm(event.target.value);
                    setCurrentPage(1);
                }}
                />
            </div>
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-52">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Status
            </label>

            <select
                className="input-base w-full"
                value={statusFilter}
                onChange={(event) => {
                setStatusFilter(
                    event.target.value as UserStatus | "All"
                );
                setCurrentPage(1);
                }}
            >
                <option value="All">All Statuses</option>

                {USER_STATUSES.map((status) => (
                <option
                    key={status}
                    value={status}
                >
                    {status}
                </option>
                ))}
            </select>
            </div>

            {/* Sort */}
            <div className="w-full lg:w-56">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                Sort By
            </label>

            <select
                className="input-base w-full"
                value={sortField}
                onChange={(event) =>
                setSortField(
                    event.target.value as SortField
                )
                }
            >
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="createdDate">Created Date</option>
            </select>
            </div>
        </div>
      </div>

      <UserTable
        users={filteredUsers}
        loading={usersQuery.isLoading}
        error={usersQuery.isError ? getApiErrorMessage(usersQuery.error, 'Unable to load users.') : ''}
        onAction={openAction}
      />
      {(usersQuery.data?.total ?? 0) > 0 && <Pagination currentPage={currentPage} totalPages={totalPages} totalUsers={usersQuery.data?.total ?? 0} onPageChange={setCurrentPage} />}

      <CreateUserModal isOpen={activeAction === 'create'} onClose={closeModal} brandingId={claims?.brandingId ?? ''} tiers={tiers} addOns={addOns} loading={mutations.createUser.isPending} error={error} onSave={(request: AdminCreateUserRequest) => {
        if (!claims?.brandingId) {
          setError('Your organization branding is required to create a user.');
          return;
        }
        runAction(() => mutations.createUser.mutateAsync(request), 'User invitation sent.');
      }} />
      <EditUserModal user={activeAction === 'edit' ? detailedUser : null} subscription={subscriptionQuery.data} pricing={pricingQuery.data} tiers={tiers} addOns={addOns} onClose={closeModal} loading={mutations.updateUser.isPending} error={error} onSave={(request) => selectedUser && runAction(() => mutations.updateUser.mutateAsync({ userId: selectedUser.id, request }), 'User updated.')} />
      <ViewUserModal user={activeAction === 'view' ? detailedUser : null} onClose={closeModal} onAudit={() => selectedUser && openAction('audit', selectedUser)} subscription={subscriptionQuery.data} pricing={pricingQuery.data} subscriptionLoading={subscriptionQuery.isLoading || pricingQuery.isLoading} onRemoveAddOn={(addOnId) => selectedUser && runAction(() => mutations.removeUserAddOn.mutateAsync({ userId: selectedUser.id, addOnId }), 'Add-on removed.')} removingAddOnId={mutations.removeUserAddOn.isPending ? mutations.removeUserAddOn.variables?.addOnId : undefined} />
      <ActivateUserModal user={activeAction === 'activate' ? detailedUser : null} onClose={closeModal} loading={isSaving} error={error} onConfirm={() => updateStatus('Active')} />
      <DeactivateUserModal user={activeAction === 'deactivate' ? detailedUser : null} onClose={closeModal} loading={isSaving} error={error} onConfirm={() => updateStatus('Inactive')} />
      <SuspendUserModal user={activeAction === 'suspend' ? detailedUser : null} onClose={closeModal} loading={isSaving} error={error} onConfirm={(reason: string | undefined) => updateStatus('Suspended', reason)} />
      <UnlockUserModal user={activeAction === 'unlock' ? detailedUser : null} onClose={closeModal} loading={isSaving} error={error} onConfirm={() => selectedUser && runAction(() => mutations.unlock.mutateAsync(selectedUser.id), 'User unlocked.')} />
      <ForcePasswordResetModal user={activeAction === 'reset' ? detailedUser : null} onClose={closeModal} loading={isSaving} error={error} onConfirm={() => selectedUser && runAction(() => mutations.forceReset.mutateAsync(selectedUser.id), 'Password reset sent.')} />
      <ResendInvitationModal user={activeAction === 'invite' ? detailedUser : null} onClose={closeModal} loading={isSaving} error={error} onConfirm={() => selectedUser && runAction(() => mutations.resendInvite.mutateAsync(selectedUser.id), 'Invitation resent.')} />
      <AssignTierModal user={activeAction === 'tier' ? detailedUser : null} subscription={subscriptionQuery.data} tiers={tiers} onClose={closeModal} loading={isSaving} error={error} onConfirm={(tierId: number) => selectedUser && runAction(() => mutations.updateUserSubscriptionTier.mutateAsync({ userId: selectedUser.id, request: { userId: selectedUser.id, tierId } }), 'Tier assigned.')} />
      <AssignAddOnsModal user={activeAction === 'addons' ? detailedUser : null} subscription={subscriptionQuery.data} addOns={addOns} onClose={closeModal} loading={isSaving} error={error} onConfirm={(addOnIds: number[]) => selectedUser && runAction(() => mutations.assignUserAddOns.mutateAsync({ userId: selectedUser.id, request: { addOnIds } }), 'Add-ons updated.')} />
      <AuditHistoryModal isOpen={activeAction === 'audit'} onClose={closeModal} records={audit.data} loading={audit.isLoading} />
    </div>
  );
}

function UserTable({
  users,
  loading,
  error,
  onAction,
}: {
  users: UserSummary[];
  loading: boolean;
  error: string;
  onAction: (action: UserAction, user: UserSummary) => void;
}) {
  if (loading) {
    return (
      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-14 text-center shadow-sm">
        <p className="font-semibold text-slate-500">
          Loading users...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-red-700">
        {error}
      </div>
    );
  }

  if (!users.length) {
    return (
      <div className="rounded-2xl border border-strivre-lightnavy bg-white p-14 text-center shadow-sm">
        <UsersRound
          size={42}
          className="mx-auto mb-3 text-strivre-lightnavy"
        />

        <p className="font-semibold text-slate-500">
          No users found
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-strivre-lightnavy bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-strivre-lightnavy bg-slate-50">
            <tr>
              {[
                'Name',
                'Email',
                'Role',
                'Status',
                'Tier',
                'Created Date',
                'Actions',
              ].map((heading) => (
                <th
                  key={heading}
                  className="px-5 py-4 text-xs font-bold uppercase tracking-wide text-slate-500"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {users.map((user) => (
              <tr
                key={user.id}
                className="transition-colors hover:bg-slate-50"
              >
                <td className="px-5 py-4">
                  <div className="font-semibold text-slate-800">
                    {user.firstName} {user.lastName}
                  </div>
                </td>

                <td className="px-5 py-4 text-slate-600">
                  {user.email}
                </td>

                <td className="px-5 py-4 text-slate-600">
                  {user.roles.join(', ')}
                </td>

                <td className="px-5 py-4">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_CLASSES[user.status]}`}
                  >
                    {user.status}
                  </span>
                </td>

                <td className="px-5 py-4">
                  {user.tierName ? (
                    <span className="inline-flex rounded-full bg-strivre-lightburgundy px-3 py-1 text-xs font-semibold text-strivre-burgundy">
                      {user.tierName}
                    </span>
                  ) : (
                    <span className="text-sm text-slate-400">
                      No Tier
                    </span>
                  )}
                </td>
                <td className="px-5 py-4 text-slate-600">
                  {new Date(
                    user.createdDate
                  ).toLocaleDateString()}
                </td>

                <td className="px-5 py-4">
                  <UserActions
                    user={user}
                    onAction={onAction}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function toUserDetails(user: UserSummary | null) {
  if (!user) return null;

  return {
    ...user,
    updatedDate: null,
    subscriptionId: null,
    passwordLastChangedDate: null,
    passwordExpirationDate: null,
    mustChangePassword: false,
    subscriptions: [],
    addOnNames: [],
  };
}

function Pagination({ currentPage, totalPages, totalUsers, onPageChange }: { currentPage: number; totalPages: number; totalUsers: number; onPageChange: (page: number) => void }) {
  return <footer className="mt-4 flex items-center justify-between rounded-2xl border border-strivre-lightnavy bg-white p-4 text-sm text-slate-500"><span>{totalUsers} user(s)</span><div className="flex items-center gap-3"><button className="btn-secondary px-3 py-1" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)}>Previous</button><span>{currentPage} / {totalPages}</span><button className="btn-secondary px-3 py-1" disabled={currentPage === totalPages} onClick={() => onPageChange(currentPage + 1)}>Next</button></div></footer>;
}

function UserActions({
  user,
  onAction,
}: {
  user: UserSummary;
  onAction: (
    action: UserAction,
    user: UserSummary
  ) => void;
}) {
  const buttonClass =
    'rounded-lg p-2 transition-all duration-200';

  return (
    <div className="flex flex-wrap gap-1">
      <button
        title="Edit User"
        className={`${buttonClass}
          text-slate-600
          hover:bg-blue-50
          hover:text-blue-600`}
        onClick={() => onAction('edit', user)}
      >
        <Pencil size={16} />
      </button>

      <button
        title="View User"
        className={`${buttonClass}
          text-slate-600
          hover:bg-blue-50
          hover:text-blue-600`}
        onClick={() => onAction('view', user)}
      >
        <Eye size={16} />
      </button>

      <button
        title={
          user.status === 'Active'
            ? 'Deactivate User'
            : 'Activate User'
        }
        className={`${buttonClass}
          text-slate-600
          hover:bg-amber-50
          hover:text-amber-600`}
        onClick={() =>
          onAction(
            user.status === 'Active'
              ? 'deactivate'
              : 'activate',
            user
          )
        }
      >
        {user.status === 'Active' ? (
          <UserRoundX size={16} />
        ) : (
          <UserRoundCheck size={16} />
        )}
      </button>

      <button
        title="Suspend User"
        className={`${buttonClass}
          text-slate-600
          hover:bg-red-50
          hover:text-red-600`}
        onClick={() => onAction('suspend', user)}
      >
        <LockKeyhole size={16} />
      </button>

      <button
        title="Unlock User"
        className={`${buttonClass}
          text-slate-600
          hover:bg-emerald-50
          hover:text-emerald-600`}
        onClick={() => onAction('unlock', user)}
      >
        <LockOpen size={16} />
      </button>

      <button
        title="Force Password Reset"
        className={`${buttonClass}
          text-slate-600
          hover:bg-purple-50
          hover:text-purple-600`}
        onClick={() => onAction('reset', user)}
      >
        <RefreshCcw size={16} />
      </button>

      <button
        title="Resend Invitation"
        className={`${buttonClass}
          text-slate-600
          hover:bg-sky-50
          hover:text-sky-600`}
        onClick={() => onAction('invite', user)}
      >
        <Mail size={16} />
      </button>

      <button
        title="Assign Tier"
        className={`${buttonClass}
          text-slate-600
          hover:bg-indigo-50
          hover:text-indigo-600`}
        onClick={() => onAction('tier', user)}
      >
        <ShieldCheck size={16} />
      </button>

      <button
        title="Assign Add-ons"
        className={`${buttonClass}
          text-slate-600
          hover:bg-strivre-softgrey
          hover:text-strivre-burgundy`}
        onClick={() => onAction('addons', user)}
      >
        <SlidersHorizontal size={16} />
      </button>
    </div>
  );
}